import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { TranslocoModule, TranslocoService } from '@ngneat/transloco';
import { SharedModule } from '@shared/shared.module';
import {
  AccountPublicApiService
} from '@/app/platform/platform-core/user/services/account-public-api.service';
import { PublicAccountShellComponent } from '../public-account-shell/public-account-shell.component';
import { EgiErrorCode } from '@data/_generated-api/error-mappers/egi-error-code';

const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * "Wachtwoord vergeten"-pagina. Toont altijd een neutrale bevestiging,
 * ongeacht of het e-mailadres bestaat (geen account-enumeratie).
 */
@Component({
  selector: 'forgot-password-page',
  imports: [FormsModule, RouterLink, TranslocoModule, SharedModule, PublicAccountShellComponent],
  templateUrl: './forgot-password-page.component.html',
  styleUrl: './forgot-password-page.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ForgotPasswordPageComponent {
  private readonly accountApi = inject(AccountPublicApiService);
  private readonly transloco = inject(TranslocoService);

  protected readonly email = signal('');
  protected readonly isSubmitting = signal(false);
  protected readonly submitted = signal(false);
  protected readonly errorKey = signal<string | null>(null);

  protected readonly emailIsValid = computed(() => EMAIL_PATTERN.test(this.email().trim()));

  /** Titel boven de foutmelding; alleen gevuld bij een mailservice-storing. */
  protected readonly errorTitleKey = computed(() =>
    this.errorKey() === 'account.mailServiceUnavailable.text'
      ? 'account.mailServiceUnavailable.title'
      : null
  );

  async onSubmit(): Promise<void> {
    if (!this.emailIsValid() || this.isSubmitting()) {
      return;
    }

    this.errorKey.set(null);
    this.isSubmitting.set(true);
    const result = await this.accountApi.forgotPassword(
      this.email().trim(),
      this.transloco.getActiveLang()
    );
    this.isSubmitting.set(false);

    result.ifSuccess(() => this.submitted.set(true));
    result.ifError(errors => {
      // Alleen een mailservice-storing mag zichtbaar zijn; al het andere blijft de
      // neutrale bevestiging (geen account-enumeratie).
      const codes = errors.map(error => error.errorCode);
      if (codes.includes(EgiErrorCode.EmailServiceUnavailable)) {
        this.errorKey.set('account.mailServiceUnavailable.text');
      } else {
        this.submitted.set(true);
      }
    });
  }
}
